import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/lib/video";

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMobileOpen(false);
  };

  const isActive = (to: string) => location.pathname === to;

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-3 px-4 md:gap-6 md:px-8">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="font-heading text-lg font-bold text-foreground-950">
            逼特逼视频
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/")
                ? "text-primary-600"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            首页
          </Link>
          <div className="group relative">
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:text-primary-600"
            >
              分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            <div className="invisible absolute left-0 top-full z-50 w-56 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                >
                  <i className={`${cat.icon} text-base text-primary-500`} />
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/search"
            className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/search")
                ? "text-primary-600"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            搜索
          </Link>
          <Link
            to="/contact"
            className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/contact")
                ? "text-primary-600"
                : "text-foreground-700 hover:text-primary-600"
            }`}
          >
            联系我们
          </Link>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden md:block">
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 transition-colors focus-within:border-primary-400">
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索视频、标签或作者"
              className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-56"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <Link
            to="/search"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-3">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索视频、标签或作者"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
            >
              首页
            </Link>
            <p className="px-3 pb-1 pt-2 text-xs text-foreground-400">视频分类</p>
            {allCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
              >
                <i className={`${cat.icon} text-base text-primary-500`} />
                {cat.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="mt-1 cursor-pointer rounded-md px-3 py-2 text-sm text-foreground-800 hover:bg-background-100"
            >
              联系我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}